import { InfoCircleOutlined } from "@ant-design/icons";
import { Alert, Flex } from "antd";
import { FC, HTMLAttributes, ReactNode, useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import { ConfigurationsList } from "./ConfigurationsList";
import styles from "./ExcelFilePreview.module.scss";
import { TablePreview } from "./TablePreview";

import { useAppSelector } from "../../store/hooks/hooks";
import { selectFileUpload } from "../../store/slices/uploadFileSlice";
import { ImportConfiguration } from "../../types/importConfiguration";
import { CustomLabel } from "../CustomLabel/CustomLabel";
import { CustomLink } from "../CustomLink/CustomLink";
import { Description } from "../Description";

interface ExcelFilePreviewProps {
  importConfigurations: ImportConfiguration[];
  selectedImportConfiguration: ImportConfiguration | null;
  setSelectedImportConfiguration: (config: ImportConfiguration | null) => void;
  handleDataSourceImportConfigurationChange: (newDataSource: ImportConfiguration[]) => void;
  handleConfigurationDelete: (config: ImportConfiguration) => void;
}

interface Step {
  title: string;
  content?: ReactNode;
  description?: ReactNode;
  onClick?: (...args: any[]) => void; // Replace `any` with the appropriate types
}

export const ExcelFilePreview: FC<ExcelFilePreviewProps> = ({
  importConfigurations,
  selectedImportConfiguration,
  setSelectedImportConfiguration,
  handleDataSourceImportConfigurationChange,
  handleConfigurationDelete
}) => {
  const [fileData, setFileData] = useState<XLSX.WorkBook | null>(null);
  const [status, setStatus] = useState<string>("initial");
  const [sheetName, setSheetName] = useState<string>("");
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  const [dataRow, setDataRow] = useState<number | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const file = useAppSelector(selectFileUpload);

  useEffect(() => {
    if (selectedImportConfiguration) {
      const { importConfigurationArguments, sourceIdentifier } = selectedImportConfiguration;
      const headerRowArg = importConfigurationArguments.find(
        (arg) => arg.identifier === "HeaderRowIndex"
      );
      const dataRowArg = importConfigurationArguments.find(
        (arg) => arg.identifier === "FirstDataRowIndex"
      );

      if (!sheetName && sourceIdentifier) {
        setSheetName(sourceIdentifier);
      }

      // If no header row is set, set it from the configuration
      if (!headerRow) {
        setHeaderRow(headerRowArg ? parseInt(headerRowArg.value, 10) : null);
      }
      
      if (!dataRow) {
        // If no data row is set, set it from the configuration
        setDataRow(dataRowArg ? parseInt(dataRowArg.value, 10) : null);
      }
    }
  }, [selectedImportConfiguration, sheetName, setHeaderRow, setDataRow]);

  useEffect(() => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const binaryStr = e.target?.result as ArrayBuffer;
      const workbook = XLSX.read(binaryStr, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      
      setStatus("idle");
      
      setSheetName(firstSheetName);
      setFileData(workbook);
    };

    setStatus("reading");
    // @ts-ignore
    reader.readAsArrayBuffer(file);
    
  }, [file]);

  const sheetOptions = useMemo(() => {
    return fileData?.SheetNames.map((sheetName) => ({
      key: sheetName,
      label: sheetName,
    }));
  }, [fileData]);

  useEffect(() => {
    if (sheetOptions?.length === 1) {
      setSheetName(sheetOptions[0].key);
    }
  }, [sheetOptions, setSheetName]);

  const markRow = (type: string | null, row: number | null) => {
    if (type === "headerRow") {
      if (row === dataRow) {
        toast.error("Header row cannot be the same as the data row.");
        return;
      }
      setHeaderRow(row);

    } else if (type === "dataRow") {
      if (row === headerRow) {
        toast.error("Data row cannot be the same as the header row.");
        return;
      }
      setDataRow(row);
    }
  };

  useEffect(() => {
    if (selectedImportConfiguration && (headerRow !== null || dataRow !== null)) {
      // Update the selected configuration with the current header and data row indices
      const updatedConfiguration = {
        ...selectedImportConfiguration,
        importConfigurationArguments: selectedImportConfiguration.importConfigurationArguments.map((arg) => {
          if (arg.identifier === "HeaderRowIndex") {
            return { ...arg, value: headerRow?.toString() || "" };
          } else if (arg.identifier === "FirstDataRowIndex") {
            return { ...arg, value: dataRow?.toString() || "" };
          }
          return arg;
        }),
      };

      setSelectedImportConfiguration(updatedConfiguration);
     }
  }, [headerRow, dataRow]);

  const handleImportConfigurationSelect = (config: ImportConfiguration) => {
    const { importConfigurationArguments } = config;
    const headerRowValue = importConfigurationArguments.find(arg => arg.identifier === "HeaderRowIndex")?.value;
    const dataRowValue = importConfigurationArguments.find(arg => arg.identifier === "FirstDataRowIndex")?.value;

    if (!headerRow && headerRowValue) {
      setHeaderRow(parseInt(headerRowValue, 10));
    }

    if (!dataRow && dataRowValue) {
      setDataRow(parseInt(dataRowValue, 10));
    }

    setSelectedImportConfiguration({
      ...config,
      sourceIdentifier: sheetName || config.sourceIdentifier,
      importConfigurationArguments: config.importConfigurationArguments.map((arg) => {
        if (arg.identifier === "HeaderRowIndex") {
          return { ...arg, value: headerRow?.toString() || "" };
        } else if (arg.identifier === "FirstDataRowIndex") {
          return { ...arg, value: dataRow?.toString() || "" };
        }
        return arg;
      }),
    });
  }

  const handleAddConfiguration = (newConfig: ImportConfiguration) => {
    handleDataSourceImportConfigurationChange([...importConfigurations, newConfig]);
  };

  if (status !== "idle") {
    return <div>Loading ...</div>;
  }

  const worksheet = fileData?.Sheets[sheetName || fileData?.SheetNames[0]] || null;

  return (
    <div className={styles.root}>
      <Flex gap={8} align="flex-start" justify="flex-start" vertical>
        <Alert showIcon icon={<InfoCircleOutlined style={{ padding: 8, transform: "scale(1.5)" }} />} message={          <>
            <Description theme="t2">
              To process this file correctly, our system needs to identify which rows contain headers and data.
            </Description>
            <Description theme="t2">
              If you have a configuration saved, you can select it from the <CustomLink
                className={styles.configurationsLink}
                onClick={(e) => {
                  e?.preventDefault();
                  document.querySelector("#configurations-list")?.scrollIntoView({ behavior: "smooth"})
                }}
              >list</CustomLink> below.
            </Description>
          </>
        }/>
        <Description theme="t4" className={styles.instructions}>
          <Flex gap={2} align="flex-start" justify="flex-start" vertical>
            <Flex gap={8} align="flex-start" justify="flex-start">
              1. Drag and   drop
              <CustomLabel
                draggable="true"
                title="Headers"
                type="table-headers"
                onDrag={(e) => {
                  e.preventDefault();
                  setDragging("headerRow");
                }}
                onDragEnd={(e) => {
                  e.preventDefault();
                  setDragging(null);
                }} 
              />
              on the headers row.
            </Flex>
            <Flex gap={8} align="center" justify="flex-start">
              2. Drag and drop
              <CustomLabel
                draggable="true"
                title="Data"
                type="table-data"
                onDrag={(e) => {
                  e.preventDefault();
                  setDragging("dataRow");
                }}
                onDragEnd={(e) => {
                  e.preventDefault();
                  setDragging(null);
                }}
              />
              on the first data row.
            </Flex>
          </Flex>
        </Description>
        <TablePreview
          worksheet={worksheet}
          sheetOptions={sheetOptions}
          sheetName={sheetName}
          onSheetChange={(name) => setSheetName(name)}
          dataRow={dataRow}
          headerRow={headerRow}
          dragging={dragging}
          action={(type, row) => {
            markRow(type || dragging, row);
          }}
        />
        <ConfigurationsList
          fileData={fileData}
          sheetName={sheetName}
          dataRow={dataRow}
          headerRow={headerRow}
          handleAddConfiguration={handleAddConfiguration}
          handleConfigurationDelete={handleConfigurationDelete}
          importConfigurations={importConfigurations}
          selectedImportConfiguration={selectedImportConfiguration}
          handleImportConfigurationSelect={handleImportConfigurationSelect}
        />
      </Flex>      
    </div>
  );
};

interface StepItemProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const StepItem: FC<StepItemProps> = ({ children, ...props }) => {
  return (
    <div {...props} className={styles.stepItem}>
      {children}
    </div>
  );
};